import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://b6ncpgrvlaefie4p3odynic3de0zjkpc.lambda-url.us-east-1.on.aws/api';

// Azure login URL from AuthHandler
const AZURE_LOGIN_URL = 'https://login.microsoftonline.com/ac1f7d2b-c741-43f6-9893-d39b22c46953/saml2?SAMLRequest=jZFNa4NAEIb%2fiuxdXY2mdomC%2bYJACiVpe%2bhtYkZisu7and18%2ffrahJwKpddh3od5nxkRtLITpbM7tcIvh2S9cysV5cwZJTRQQ0JBiyRsJdbly1LEARctWtiCBeYtpjmbZ8k0y7KyHIyTZBKNsyQZPKVlMsviiE%2bfZ8z7QEONVjnrw32GyOFCkQVl%2bxGPU58PfR69RVzwoeBpwJP482fvFYiaI%2basBknIvJIIje1JE63ItWjWaI5Nhe%2brZc521nYkwvBwlecBni9um7rd4Xo8nExdp92%2bBg7G7WUgod1swXdGBo58BLJ%2bFGgVwIlC6JoQehthBVJuoDqwuw9xE%2fW3lc5oqystmTfXpsKb1MfpxejW2vzHLjxasuLRqdv1aNdJDVs0QXtB2ASVbkfhnVqMwt%2bPLL4B';

// Function to handle 401 errors and redirect to login
const handleUnauthorized = () => {
  // Clear all auth-related data from sessionStorage
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('userEmail');
  sessionStorage.removeItem('userGivenName');
  sessionStorage.removeItem('userSurname');
  
  // Show alert before redirecting
  alert('Your session has expired. You will be redirected to the login page.');
  
  // Redirect to Azure login
  window.location.href = AZURE_LOGIN_URL;
};

// Function to handle streaming responses from the photo upload API
export const uploadPhoto = async (formData, onEventCallback) => {
  const token = sessionStorage.getItem('authToken');
  
  // Check if token exists before making the request
  if (!token) {
    handleUnauthorized();
    return;
  }
  
  try {
    // Create a response that can process streaming data
    const response = await fetch(`${API_BASE_URL}/photos/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }).catch(fetchError => {
      // Handle network-level errors (including CORS issues with 401s)
      console.error('Fetch error:', fetchError);
      
      // Check if it's a 413 error at the network level
      if (fetchError.message && (fetchError.message.includes('413') || fetchError.message.includes('Entity Too Large'))) {
        throw new Error('Image seems to be corrupted and unable to process or validate. Please change the image.');
      }
      
      // If fetch fails completely, assume it might be auth-related
      handleUnauthorized();
      throw new Error('Authentication failed. Redirecting to login...');
    });
    
    // Check for 401 Unauthorized
    if (response && response.status === 401) {
      handleUnauthorized();
      return;
    }
    
    // Check for 413 Forbidden - corrupted/invalid image
    if (response && response.status === 413) {
      throw new Error('Image seems to be corrupted and unable to process or validate. Please change the image.');
    }
    
    if (!response || !response.ok) {
      const errorText = response ? await response.text() : 'Network error';
      throw new Error(errorText || `Server error: ${response?.status}`);
    }
    
    // Get the reader from the response body
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    let buffer = '';
    
    // Function to read chunks from the stream
    async function readChunks() {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in the buffer
          if (buffer.trim()) {
            processEvent(buffer);
          }
          break;
        }
        
        // Decode and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process any complete JSON objects in the buffer
        const lines = buffer.split('\n');
        
        // Process all complete lines except the last one (which might be incomplete)
        for (let i = 0; i < lines.length - 1; i++) {
          if (lines[i].trim()) {
            processEvent(lines[i]);
          }
        }
        
        // Keep the last line in the buffer
        buffer = lines[lines.length - 1];
      }
    }
    
    // Process a single event from the stream
    function processEvent(eventString) {
      try {
        const event = JSON.parse(eventString);
        if (onEventCallback && typeof onEventCallback === 'function') {
          onEventCallback(event);
        }
      } catch (error) {
        console.error('Error parsing event:', error, eventString);
      }
    }
    
    // Start reading the stream
    await readChunks();
    
    return true;
  } catch (error) {
    // Check if the error is due to 401 Unauthorized
    // This can happen if the server immediately rejects with 401
    if (error.message && error.message.includes('401')) {
      handleUnauthorized();
      return;
    }
    
    // Also check if fetch failed due to network/CORS issues that might be auth-related
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      // Try to determine if this is an auth issue by checking if we have a token
      handleUnauthorized();
      return;
    }
    
    console.error('Error uploading photo:', error);
    throw error;
  }
};

// Function to preview photo without uploading to S3
export const previewPhoto = async (formData) => {
  const token = sessionStorage.getItem('authToken');
  
  // Check if token exists before making the request
  if (!token) {
    handleUnauthorized();
    return;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/photos/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    // Check for 401 Unauthorized
    if (response && response.status === 401) {
      handleUnauthorized();
      return;
    }
    
    // Check for 413 or other errors
    if (response && response.status === 413) {
      throw new Error('Image seems to be corrupted and unable to process or validate. Please change the image.');
    }
    
    if (!response || !response.ok) {
      const errorText = response ? await response.text() : 'Network error';
      let errorDetail;
      try {
        const errorJson = JSON.parse(errorText);
        errorDetail = errorJson.detail || errorText;
      } catch {
        errorDetail = errorText;
      }
      // Create error with proper structure
      const error = new Error(Array.isArray(errorDetail) ? 'Validation failed' : (errorDetail || `Server error: ${response?.status}`));
      error.validationErrors = Array.isArray(errorDetail) ? errorDetail : null;
      error.detail = errorDetail;
      throw error;
    }
    
    // Parse JSON response
    const data = await response.json();
    return data;
    
  } catch (error) {
    // Check if the error is due to 401 Unauthorized
    if (error.message && error.message.includes('401')) {
      handleUnauthorized();
      return;
    }
    
    console.error('Error previewing photo:', error);
    throw error;
  }
};

// Function to confirm upload with the same cropped file (optimized version)
// Now sends the cropped file directly instead of base64 images
export const confirmUpload = async (croppedFile) => {
  const token = sessionStorage.getItem('authToken');
  
  // Check if token exists before making the request
  if (!token) {
    handleUnauthorized();
    return;
  }
  
  try {
    // Create FormData with the cropped file
    const formData = new FormData();
    formData.append('photo', croppedFile);
    
    const response = await fetch(`${API_BASE_URL}/photos/confirm-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type - let the browser set it with boundary for multipart
      },
      body: formData,
    });
    
    // Check for 401 Unauthorized
    if (response && response.status === 401) {
      handleUnauthorized();
      return;
    }
    
    if (!response || !response.ok) {
      const errorText = response ? await response.text() : 'Network error';
      let errorDetail;
      try {
        const errorJson = JSON.parse(errorText);
        errorDetail = errorJson.detail || errorText;
      } catch {
        errorDetail = errorText;
      }
      // Create error with proper structure
      const error = new Error(Array.isArray(errorDetail) ? 'Upload failed' : (errorDetail || `Server error: ${response?.status}`));
      error.validationErrors = Array.isArray(errorDetail) ? errorDetail : null;
      error.detail = errorDetail;
      throw error;
    }
    
    // Parse JSON response
    const data = await response.json();
    return data;
    
  } catch (error) {
    // Check if the error is due to 401 Unauthorized
    if (error.message && error.message.includes('401')) {
      handleUnauthorized();
      return;
    }
    
    console.error('Error confirming upload:', error);
    throw error;
  }
};

// Health check function
export const checkHealth = async () => {
  const token = sessionStorage.getItem('authToken');
  
  // If no token, return unauthorized
  if (!token) {
    console.log('Health check: No token found');
    return { isHealthy: false, isAuthorized: false };
  }
  
  try {
    console.log('Health check: Checking token validity...');
    const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      timeout: 5000, // 5 second timeout
    });
    
    console.log('Health check: Success', response.data);
    return { isHealthy: true, isAuthorized: true, data: response.data };
  } catch (error) {
    // Check if it's a 401 unauthorized error
    if (error.response && error.response.status === 401) {
      console.log('Health check: Unauthorized (401)');
      return { isHealthy: true, isAuthorized: false };
    }
    // Check for timeout
    if (error.code === 'ECONNABORTED') {
      console.error('Health check: Request timeout');
      return { isHealthy: false, isAuthorized: false, error: 'Request timeout' };
    }
    // Other errors (network issues, server down, etc.)
    console.error('Health check: Error', error.message);
    return { isHealthy: false, isAuthorized: false, error: error.message };
  }
};

// Regular axios instance for other API calls
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 and 413 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      handleUnauthorized();
    }
    // Handle 413 Forbidden - corrupted/invalid image
    if (error.response && error.response.status === 413) {
      error.message = 'Image seems to be corrupted and unable to process or validate. Please change the image.';
    }
    return Promise.reject(error);
  }
);

export default api; 